import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useFechar } from '../input/useAtalhos';

/** O raio do anel (do centro ao centro de cada botão). */
const RAIO = 92;
/** Folga do anel até a borda da tela: o raio, meio botão e o rótulo embaixo. */
const FOLGA = RAIO + 46;

export interface BotaoRadial {
  id: string;
  rotulo: string;
  icone: ReactNode;
  /** O motivo de não valer agora; vazio = vale. */
  motivo?: string;
  /** Vermelho (Denunciar): o último do anel. */
  perigo?: boolean;
  onClick?(): void;
}

/**
 * O menu em anel em volta do clique (docs/prototipos/menu-jogador-prototipo.html, opção C): o título em cima, as ações
 * em anel, o que não vale agora fica apagado e diz o motivo no meio. Usado no jogador e no manequim de treino
 * (techspec-marceneiro-e-oficios-em-casa §06).
 */
export function MenuRadial({ titulo, sub, cor, x, y, botoes, onClose }: {
  titulo: ReactNode;
  sub?: string;
  cor?: string;
  x: number;
  y: number;
  botoes: readonly BotaoRadial[];
  onClose(): void;
}) {
  useFechar(onClose, { modal: true });
  const ref = useRef<HTMLDivElement>(null);
  const [dica, setDica] = useState<string>('');
  useEffect(() => {
    const close = () => onClose();
    const later = window.setTimeout(() => window.addEventListener('click', close), 0);
    window.addEventListener('scroll', close, true);
    return () => {
      window.clearTimeout(later);
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [onClose]);
  useEffect(() => { ref.current?.querySelector<HTMLButtonElement>('button:not([aria-disabled])')?.focus({ preventScroll: true }); }, []);

  // O anel inteiro cabe na tela; tela estreita (celular): vai para o meio, longe dos botões flutuantes das bordas.
  const cx = window.innerWidth < 520 ? window.innerWidth / 2 : Math.max(FOLGA, Math.min(x, window.innerWidth - FOLGA));
  const cy = Math.max(FOLGA + 36, Math.min(y, window.innerHeight - FOLGA - 44));
  const rotuloAria = typeof titulo === 'string' ? titulo : undefined;

  return (
    <div ref={ref} className="pm-radial" role="menu" aria-label={rotuloAria} style={{ left: cx, top: cy }} onClick={e => e.stopPropagation()}>
      <div className="pm-radial__anel" aria-hidden="true" />
      <div className="pm-radial__nome" style={cor ? { color: cor } : undefined}>
        {titulo}
        {sub && <small>{sub}</small>}
      </div>
      <div className={`pm-radial__dica${dica ? ' is-on' : ''}`} aria-live="polite">{dica}</div>
      {botoes.map((b, i) => {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / botoes.length;
        const off = !!b.motivo;
        return (
          <button
            key={b.id}
            type="button"
            role="menuitem"
            className={`pm-radial__b${off ? ' is-off' : ''}${b.perigo ? ' is-perigo' : ''}`}
            style={{ left: Math.round(Math.cos(a) * RAIO), top: Math.round(Math.sin(a) * RAIO) }}
            aria-disabled={off || undefined}
            aria-label={off ? `${b.rotulo} · ${b.motivo}` : b.rotulo}
            onMouseEnter={() => setDica(off ? b.motivo! : b.rotulo)}
            onFocus={() => setDica(off ? b.motivo! : b.rotulo)}
            onMouseLeave={() => setDica('')}
            onClick={() => { if (!off) b.onClick?.(); }}
          >
            {b.icone}
            <span>{b.rotulo}</span>
          </button>
        );
      })}
    </div>
  );
}
